import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  LogOut, Users, Briefcase, AlertTriangle, Search,
  Loader2, User, Wrench, Plus, Mail, Phone, Building, ClipboardCheck, Shield,
  RefreshCw, Trash2, Clock, Check, X, PoundSterling, Home, Send, FileText, Calendar, BarChart3
} from "lucide-react";
import { AllHoursIcon } from "@/components/AllHoursLogo";
import { API } from "@/App";

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invites, setInvites] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("contractors");
  
  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    phone: "",
    categories: []
  });

  // Move-In Pack modal state
  const [moveInPackModalOpen, setMoveInPackModalOpen] = useState(false);
  const [moveInPackLoading, setMoveInPackLoading] = useState(false);
  const [moveInPackForm, setMoveInPackForm] = useState({
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    property_address: "",
    postcode: "",
    move_in_date: "",
    rent_amount: "",
    rent_due_day: "1",
    landlord_name: "",
    additional_notes: ""
  });

  // Audit trail state
  const [auditPacks, setAuditPacks] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const CATEGORIES = [
    "Heating", "Plumbing", "Electrical", "Appliance", "Damp", "Locks", "General"
  ];

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, contractorsRes, tenantsRes, jobsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/contractors`, { headers }),
        axios.get(`${API}/admin/tenants`, { headers }),
        axios.get(`${API}/admin/jobs`, { headers }),
        axios.get(`${API}/admin/invites`, { headers })
      ]);

      setStats(statsRes.data);
      setContractors(contractorsRes.data.contractors || []);
      setTenants(tenantsRes.data.tenants || []);
      setJobs(jobsRes.data.jobs || []);
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInviteContractor = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error("Name and email are required");
      return;
    }

    setInviteLoading(true);
    try {
      await axios.post(
        `${API}/admin/invite-contractor`,
        inviteForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invite sent to ${inviteForm.email}`);
      setInviteModalOpen(false);
      setInviteForm({ name: "", email: "", phone: "", categories: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(
        `${API}/admin/resend-invite/${inviteId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Invite resent");
    } catch (error) {
      toast.error("Failed to resend invite");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/admin/invites/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invite deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invite");
    }
  };

  const handleSendMoveInPack = async () => {
    // Validate required fields
    if (!moveInPackForm.tenant_name || !moveInPackForm.tenant_email || 
        !moveInPackForm.property_address || !moveInPackForm.postcode || 
        !moveInPackForm.move_in_date || !moveInPackForm.rent_amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setMoveInPackLoading(true);
    try {
      const payload = {
        ...moveInPackForm,
        rent_amount: parseFloat(moveInPackForm.rent_amount),
        rent_due_day: parseInt(moveInPackForm.rent_due_day) || 1
      };

      await axios.post(
        `${API}/tenants/send-move-in-pack`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Move-In Pack sent to ${moveInPackForm.tenant_email}`, {
        description: "The tenant will receive a welcome email with all the move-in information"
      });
      setMoveInPackModalOpen(false);
      setMoveInPackForm({
        tenant_name: "",
        tenant_email: "",
        tenant_phone: "",
        property_address: "",
        postcode: "",
        move_in_date: "",
        rent_amount: "",
        rent_due_day: "1",
        landlord_name: "",
        additional_notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send Move-In Pack");
    } finally {
      setMoveInPackLoading(false);
    }
  };

  const prefillMoveInPackForTenant = (tenant) => {
    setMoveInPackForm({
      ...moveInPackForm,
      tenant_name: tenant.name || "",
      tenant_email: tenant.email || "",
      tenant_phone: tenant.phone || ""
    });
    setMoveInPackModalOpen(true);
  };

  const fetchAuditTrail = async () => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/tenants/move-in-packs/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditPacks(response.data.packs || []);
    } catch (error) {
      console.error("Error fetching audit trail:", error);
      toast.error("Failed to load audit trail");
    } finally {
      setAuditLoading(false);
    }
  };

  // Fetch audit trail when switching to audit tab
  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditTrail();
    }
  }, [activeTab]);

  const toggleCategory = (cat) => {
    setInviteForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Logged out");
    navigate("/login");
  };

  const filteredJobs = jobs.filter(job => 
    job.issue?.issue_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.job_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingInvites = invites.filter(i => i.status === "pending");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <AllHoursIcon size={40} />
            <div>
              <p className="text-sm text-muted-foreground">Admin Console</p>
              <h1 className="text-xl font-semibold font-outfit">All Hours</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate("/admin/lettings")} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="lettings-btn"
            >
              <Building className="w-4 h-4 mr-2" />
              Lettings
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/maintenance/analytics")} data-testid="maintenance-analytics-btn">
              <BarChart3 className="w-4 h-4 mr-2" />
              Maintenance Analytics
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/inspections")} data-testid="inspections-btn">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Inspections
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/compliance")} data-testid="compliance-btn">
              <Shield className="w-4 h-4 mr-2" />
              Compliance
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/aml-verification")} data-testid="aml-btn">
              <FileText className="w-4 h-4 mr-2" />
              AML
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/payments")} data-testid="payments-btn">
              <PoundSterling className="w-4 h-4 mr-2" />
              Payments
            </Button>
            {(user?.role === "director" || user?.role === "admin") && (
              <Button 
                onClick={() => navigate("/sales")} 
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="sales-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Sales
              </Button>
            )}
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
            onClick={() => setActiveTab("jobs")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.issues?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Issues</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-amber-300 hover:shadow-md transition-all"
            onClick={() => setActiveTab("jobs")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.jobs?.pending || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-green-300 hover:shadow-md transition-all"
            onClick={() => setActiveTab("contractors")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.users?.contractors || 0}</p>
                  <p className="text-sm text-muted-foreground">Contractors</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
            onClick={() => setActiveTab("tenants")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.users?.tenants || 0}</p>
                  <p className="text-sm text-muted-foreground">Tenants</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Emergency Alert */}
        {stats?.issues?.emergency > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-red-800">
                  {stats.issues.emergency} Emergency Issues
                </p>
                <p className="text-sm text-red-600">Require immediate attention</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="contractors" data-testid="tab-contractors">
              <Wrench className="w-4 h-4 mr-2" />
              Contractors ({contractors.length})
            </TabsTrigger>
            <TabsTrigger value="tenants" data-testid="tab-tenants">
              <Users className="w-4 h-4 mr-2" />
              Tenants ({stats?.users?.tenants || 0})
            </TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">
              <Mail className="w-4 h-4 mr-2" />
              Invites ({pendingInvites.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <FileText className="w-4 h-4 mr-2" />
              Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Contractors Tab */}
          <TabsContent value="contractors" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Contractor Directory</h2>
              <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="invite-contractor-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Invite Contractor
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Invite Contractor</DialogTitle>
                    <DialogDescription>
                      Send an email invitation to join your contractor network.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="contractor@example.com"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        placeholder="+44 7xxx xxxxxx"
                        value={inviteForm.phone}
                        onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                        data-testid="invite-phone-input"
                      />
                    </div>
                    <div>
                      <Label>Specialties (optional)</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {CATEGORIES.map((cat) => (
                          <Badge
                            key={cat}
                            variant={inviteForm.categories.includes(cat) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleCategory(cat)}
                          >
                            {inviteForm.categories.includes(cat) && <Check className="w-3 h-3 mr-1" />}
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setInviteModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleInviteContractor} 
                      disabled={inviteLoading}
                      data-testid="send-invite-btn"
                    >
                      {inviteLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Send Invite
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {contractors.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No contractors yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Invite contractors to join your network
                  </p>
                  <Button onClick={() => setInviteModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Invite First Contractor
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contractors.map((contractor) => (
                  <Card key={contractor.user_id} data-testid={`contractor-${contractor.user_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{contractor.name}</p>
                          <p className="text-sm text-muted-foreground">{contractor.email}</p>
                          {contractor.phone && (
                            <p className="text-sm text-muted-foreground">{contractor.phone}</p>
                          )}
                          {contractor.contractor_profile?.categories?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {contractor.contractor_profile.categories.map((cat) => (
                                <Badge key={cat} variant="outline" className="text-xs">
                                  {cat}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {contractor.contractor_profile?.emergency_available && (
                            <Badge className="mt-2 bg-amber-100 text-amber-700">
                              Emergency Available
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Tenant Directory</h2>
              <Button 
                onClick={() => setMoveInPackModalOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-move-in-pack-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Move-In Pack
              </Button>
            </div>
            
            {tenants.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No tenants registered yet</p>
                  <Button 
                    onClick={() => setMoveInPackModalOpen(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send First Move-In Pack
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tenants.map((tenant) => (
                  <Card key={tenant.user_id} data-testid={`tenant-${tenant.user_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{tenant.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{tenant.email}</p>
                          {tenant.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <Phone className="w-3 h-3" />
                              {tenant.phone}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">
                              {tenant.issue_count || 0} issues reported
                            </Badge>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="mt-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 p-0 h-auto"
                            onClick={() => prefillMoveInPackForTenant(tenant)}
                            data-testid={`send-pack-${tenant.user_id}`}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Send Move-In Pack
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Pending Invitations</h2>
              <Button onClick={() => setInviteModalOpen(true)} data-testid="invite-btn-2">
                <Plus className="w-4 h-4 mr-2" />
                New Invite
              </Button>
            </div>

            {invites.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No invites sent yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {invites.map((invite) => (
                  <Card key={invite.invite_id} data-testid={`invite-${invite.invite_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            invite.status === "pending" ? "bg-amber-100" :
                            invite.status === "accepted" ? "bg-green-100" : "bg-slate-100"
                          }`}>
                            {invite.status === "pending" ? (
                              <Clock className="w-5 h-5 text-amber-600" />
                            ) : invite.status === "accepted" ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : (
                              <X className="w-5 h-5 text-slate-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{invite.name}</p>
                            <p className="text-sm text-muted-foreground">{invite.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Sent {new Date(invite.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            invite.status === "pending" ? "secondary" :
                            invite.status === "accepted" ? "default" : "outline"
                          }>
                            {invite.status}
                          </Badge>
                          {invite.status === "pending" && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResendInvite(invite.invite_id)}
                                title="Resend invite"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteInvite(invite.invite_id)}
                                title="Delete invite"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-jobs"
              />
            </div>

            {/* Jobs Table */}
            <Card>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Issue Type</th>
                      <th>Tenant</th>
                      <th>Contractor</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-muted-foreground">
                          No jobs found
                        </td>
                      </tr>
                    ) : (
                      filteredJobs.map((job) => (
                        <tr 
                          key={job.job_id} 
                          data-testid={`admin-job-${job.job_id}`}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => navigate(`/admin/job/${job.job_id}`)}
                        >
                          <td className="font-mono text-sm">
                            #{job.job_id?.slice(-6)}
                          </td>
                          <td>
                            <Badge variant="secondary">{job.issue?.issue_type}</Badge>
                          </td>
                          <td>
                            <div>
                              <p className="font-medium">{job.tenant?.name}</p>
                              <p className="text-xs text-muted-foreground">{job.tenant?.email}</p>
                            </div>
                          </td>
                          <td>
                            {job.contractor ? (
                              <div>
                                <p className="font-medium">{job.contractor?.name}</p>
                                <p className="text-xs text-muted-foreground">{job.contractor?.email}</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Unassigned</span>
                            )}
                          </td>
                          <td>
                            <Badge className={
                              job.status === "completed" ? "bg-green-100 text-green-700" :
                              job.status === "pending" ? "bg-amber-100 text-amber-700" :
                              job.issue?.is_emergency ? "bg-red-100 text-red-700" :
                              "bg-blue-100 text-blue-700"
                            }>
                              {job.status?.replace(/_/g, " ")}
                            </Badge>
                          </td>
                          <td className="text-sm text-muted-foreground">
                            {new Date(job.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Move-In Pack Audit Trail</h2>
              <Button 
                variant="outline" 
                onClick={fetchAuditTrail}
                disabled={auditLoading}
                data-testid="refresh-audit-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${auditLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {auditLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : auditPacks.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No Move-In Packs sent yet</h3>
                  <p className="text-sm text-muted-foreground">
                    When you send Move-In Packs to tenants, they'll appear here for tracking.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Date Sent</th>
                        <th>Tenant</th>
                        <th>Property</th>
                        <th>Move-in Date</th>
                        <th>Sent By</th>
                        <th>Registration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditPacks.map((pack) => (
                        <tr key={pack.pack_id} data-testid={`audit-${pack.pack_id}`}>
                          <td className="text-sm">
                            <div className="font-medium">
                              {new Date(pack.sent_at).toLocaleDateString('en-GB', { 
                                day: 'numeric', month: 'short', year: 'numeric' 
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(pack.sent_at).toLocaleTimeString('en-GB', { 
                                hour: '2-digit', minute: '2-digit' 
                              })}
                            </div>
                          </td>
                          <td>
                            <div className="font-medium">{pack.tenant_name}</div>
                            <div className="text-xs text-muted-foreground">{pack.tenant_email}</div>
                          </td>
                          <td>
                            <div className="max-w-[200px] truncate" title={pack.property_address}>
                              {pack.property_address}
                            </div>
                            <div className="text-xs text-muted-foreground">{pack.postcode}</div>
                          </td>
                          <td className="text-sm">
                            {new Date(pack.move_in_date).toLocaleDateString('en-GB', { 
                              day: 'numeric', month: 'short', year: 'numeric' 
                            })}
                          </td>
                          <td>
                            <div className="text-sm">{pack.sent_by_name}</div>
                            <div className="text-xs text-muted-foreground">{pack.sent_by_email}</div>
                          </td>
                          <td>
                            {pack.registration_status === "registered" ? (
                              <div>
                                <Badge className="bg-green-100 text-green-700">
                                  <Check className="w-3 h-3 mr-1" />
                                  Registered
                                </Badge>
                                {pack.registered_at && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {new Date(pack.registered_at).toLocaleDateString('en-GB')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            
            {/* Summary Stats */}
            {auditPacks.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{auditPacks.length}</p>
                    <p className="text-sm text-muted-foreground">Total Sent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {auditPacks.filter(p => p.registration_status === "registered").length}
                    </p>
                    <p className="text-sm text-muted-foreground">Registered</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {auditPacks.filter(p => p.registration_status === "pending").length}
                    </p>
                    <p className="text-sm text-muted-foreground">Pending</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Move-In Pack Modal */}
        <Dialog open={moveInPackModalOpen} onOpenChange={setMoveInPackModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Send Move-In Pack
              </DialogTitle>
              <DialogDescription>
                Send a welcome email to a new tenant with move-in information, checklist, and portal registration link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Tenant Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm text-slate-700">Tenant Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pack_tenant_name">Tenant Name *</Label>
                    <Input
                      id="pack_tenant_name"
                      placeholder="John Smith"
                      value={moveInPackForm.tenant_name}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, tenant_name: e.target.value })}
                      data-testid="pack-tenant-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pack_tenant_email">Email Address *</Label>
                    <Input
                      id="pack_tenant_email"
                      type="email"
                      placeholder="tenant@example.com"
                      value={moveInPackForm.tenant_email}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, tenant_email: e.target.value })}
                      data-testid="pack-tenant-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pack_tenant_phone">Phone (optional)</Label>
                  <Input
                    id="pack_tenant_phone"
                    placeholder="+44 7xxx xxxxxx"
                    value={moveInPackForm.tenant_phone}
                    onChange={(e) => setMoveInPackForm({ ...moveInPackForm, tenant_phone: e.target.value })}
                    data-testid="pack-tenant-phone"
                  />
                </div>
              </div>

              {/* Property Info */}
              <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm text-emerald-700">Property Details</h4>
                <div>
                  <Label htmlFor="pack_property_address">Property Address *</Label>
                  <Input
                    id="pack_property_address"
                    placeholder="123 High Street, Apartment 4B"
                    value={moveInPackForm.property_address}
                    onChange={(e) => setMoveInPackForm({ ...moveInPackForm, property_address: e.target.value })}
                    data-testid="pack-property-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pack_postcode">Postcode *</Label>
                    <Input
                      id="pack_postcode"
                      placeholder="SW1A 1AA"
                      value={moveInPackForm.postcode}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, postcode: e.target.value })}
                      data-testid="pack-postcode"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pack_landlord_name">Landlord Name</Label>
                    <Input
                      id="pack_landlord_name"
                      placeholder="Mr. Landlord"
                      value={moveInPackForm.landlord_name}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, landlord_name: e.target.value })}
                      data-testid="pack-landlord-name"
                    />
                  </div>
                </div>
              </div>

              {/* Tenancy Info */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm text-blue-700">Tenancy Details</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="pack_move_in_date">Move-in Date *</Label>
                    <Input
                      id="pack_move_in_date"
                      type="date"
                      value={moveInPackForm.move_in_date}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, move_in_date: e.target.value })}
                      data-testid="pack-move-in-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pack_rent_amount">Monthly Rent (£) *</Label>
                    <Input
                      id="pack_rent_amount"
                      type="number"
                      step="0.01"
                      placeholder="1200.00"
                      value={moveInPackForm.rent_amount}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, rent_amount: e.target.value })}
                      data-testid="pack-rent-amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pack_rent_due_day">Rent Due Day</Label>
                    <Input
                      id="pack_rent_due_day"
                      type="number"
                      min="1"
                      max="28"
                      placeholder="1"
                      value={moveInPackForm.rent_due_day}
                      onChange={(e) => setMoveInPackForm({ ...moveInPackForm, rent_due_day: e.target.value })}
                      data-testid="pack-rent-due-day"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <Label htmlFor="pack_additional_notes">Additional Notes (optional)</Label>
                <Textarea
                  id="pack_additional_notes"
                  className="min-h-[80px]"
                  placeholder="Any specific instructions or notes for the tenant (e.g., key collection details, parking info)..."
                  value={moveInPackForm.additional_notes}
                  onChange={(e) => setMoveInPackForm({ ...moveInPackForm, additional_notes: e.target.value })}
                  data-testid="pack-additional-notes"
                />
              </div>

              {/* What's Included Info */}
              <div className="bg-amber-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-800 mb-1">📧 The email will include:</p>
                <ul className="text-amber-700 text-xs space-y-0.5 ml-4 list-disc">
                  <li>Welcome message with property details</li>
                  <li>Move-in checklist (utilities, council tax, etc.)</li>
                  <li>Emergency contact numbers</li>
                  <li>Link to register for the tenant portal</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveInPackModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendMoveInPack} 
                disabled={moveInPackLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-pack-submit-btn"
              >
                {moveInPackLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Move-In Pack
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};
